import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { 
  Plus,
  Search,
  Filter,
  Inbox,
  X
} from 'lucide-react';
import { useTickets } from '../../hooks/support/useSupportData';
import { SupportTabLayout } from './ui';
import TicketsTable from './TicketsTable';
import TicketFilters from './TicketFilters';
import Pagination from './Pagination';
import CreateTicketModal from './CreateTicketModal';

const TicketsTab = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('created_at');
  const [sortDirection, setSortDirection] = useState('desc');
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  
  // Filter state
  const [activeFilters, setActiveFilters] = useState({
    status: [],
    priority: [],
    assignee: '',
    dateFrom: '',
    dateTo: '',
    search: ''
  });
  
  // API filters state
  const [filters, setFilters] = useState({
    skip: 0,
    limit: 20,
    sort: '-created_at'
  });

  // Fetch tickets using React Query
  const { data, isLoading: loading, error } = useTickets(filters);
  
  const tickets = data?.items || [];
  const total = data?.total || 0;

  // Mock team members - in production, fetch from API
  const teamMembers = [
    { user_id: '1', name: 'John Doe' },
    { user_id: '2', name: 'Jane Smith' },
    { user_id: '3', name: 'Bob Wilson' }
  ];

  // Mock users for requester selection - in production, fetch from API
  const mockUsers = [
    { id: '1', name: 'Alice Johnson', email: 'alice@example.com' },
    { id: '2', name: 'Bob Smith', email: 'bob@example.com' },
    { id: '3', name: 'Charlie Davis', email: 'charlie@example.com' },
    { id: '4', name: 'Diana Wilson', email: 'diana@example.com' }
  ];

  // Mock available tags - in production, fetch from API
  const availableTags = ['urgent', 'bug', 'feature', 'question', 'billing', 'account'];

  // Update API filters when activeFilters, pagination, or sorting change
  useEffect(() => {
    const apiFilters = {
      skip: currentPage * itemsPerPage,
      limit: itemsPerPage,
      sort: `${sortDirection === 'desc' ? '-' : ''}${sortBy}`
    };

    // Add status filter
    if (activeFilters.status.length > 0) {
      apiFilters.status = activeFilters.status.join(',');
    }

    // Add priority filter
    if (activeFilters.priority.length > 0) {
      apiFilters.priority = activeFilters.priority.join(',');
    }

    // Add assignee filter
    if (activeFilters.assignee) {
      if (activeFilters.assignee === 'unassigned') {
        apiFilters.assignee = 'null';
      } else {
        apiFilters.assignee = activeFilters.assignee;
      }
    }

    // Add date range filter
    if (activeFilters.dateFrom) {
      apiFilters.created_after = activeFilters.dateFrom;
    }
    if (activeFilters.dateTo) {
      apiFilters.created_before = activeFilters.dateTo;
    }

    // Add search filter
    if (searchQuery) {
      apiFilters.search = searchQuery;
    }

    setFilters(apiFilters);
  }, [activeFilters, sortBy, sortDirection, searchQuery, currentPage, itemsPerPage]);

  // Count active filters
  const getActiveFilterCount = () => {
    let count = 0;
    if (activeFilters.status.length > 0) count++;
    if (activeFilters.priority.length > 0) count++;
    if (activeFilters.assignee) count++;
    if (activeFilters.dateFrom || activeFilters.dateTo) count++;
    return count;
  };

  const activeFilterCount = getActiveFilterCount();

  // Handle sort
  const handleSort = (field) => {
    if (sortBy === field) {
      // Toggle direction
      const newDirection = sortDirection === 'asc' ? 'desc' : 'asc';
      setSortDirection(newDirection);
    } else {
      // New field, default to descending
      setSortBy(field);
      setSortDirection('desc');
    }
  };

  // Handle ticket click
  const handleTicketClick = (ticket) => {
    setSelectedTicket(ticket);
    // TODO: Navigate to ticket detail view or open modal
    console.log('Ticket clicked:', ticket);
  };

  // Handle create ticket
  const handleCreateTicket = () => {
    setIsCreateModalOpen(true);
  };

  // Handle ticket submission
  const handleTicketSubmit = async (ticketData) => {
    // TODO: Call API to create ticket
    console.log('Creating ticket:', ticketData);
    
    // For now, just simulate API call
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    // Refetch tickets after creation
    // React Query will automatically refetch
    
    // Show success message (TODO: add toast notification)
    alert('Ticket created successfully!');
  };

  // Handle filters change
  const handleFiltersChange = (newFilters) => {
    setActiveFilters(newFilters);
    setCurrentPage(0); // Reset to first page when filters change
  };

  // Handle clear filters
  const handleClearFilters = () => {
    setActiveFilters({
      status: [],
      priority: [],
      assignee: '',
      dateFrom: '',
      dateTo: '',
      search: ''
    });
    setSearchQuery('');
    setCurrentPage(0); // Reset to first page
  };

  // Handle page change
  const handlePageChange = (page) => {
    setCurrentPage(page);
    // Scroll to top of table
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Handle items per page change
  const handleItemsPerPageChange = (newItemsPerPage) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(0); // Reset to first page when changing items per page
  };

  // Handle search with debounce
  useEffect(() => {
    const timer = setTimeout(() => {
      setActiveFilters(prev => ({ ...prev, search: searchQuery }));
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Empty state (no tickets at all)
  if (tickets.length === 0 && !searchQuery && !loading) {
    return (
      <SupportTabLayout error={error}>
        <div className="text-center py-12">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gray-100 dark:bg-gray-800 mb-4">
            <Inbox className="w-8 h-8 text-gray-400" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
            No tickets yet
          </h3>
          <p className="text-gray-600 dark:text-gray-400 mb-6 max-w-md mx-auto">
            When customers create support tickets, they will appear here. You can also create tickets manually.
          </p>
          <Button onClick={handleCreateTicket}>
            <Plus className="w-4 h-4 mr-2" />
            Create First Ticket
          </Button>
        </div>
      </SupportTabLayout>
    );
  }

  return (
    <SupportTabLayout error={error}>
      <div className="space-y-6">
        {/* Header with Stats */}
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
              Support Tickets
            </h2>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              {total} total {total === 1 ? 'ticket' : 'tickets'}
              {activeFilterCount > 0 && ` (${activeFilterCount} filter${activeFilterCount > 1 ? 's' : ''} active)`}
            </p>
          </div>
          
          {/* Create Ticket Button */}
          <Button onClick={handleCreateTicket}>
            <Plus className="w-4 h-4 mr-2" />
            Create Ticket
          </Button>
        </div>

        {/* Filters and Search */}
        <div className="flex items-center gap-4">
          {/* Search */}
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search tickets by ID, subject, or requester..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-10 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent dark:bg-gray-800 dark:border-gray-700 dark:text-gray-100"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
              >
                <X className="w-4 h-4 text-gray-400" />
              </button>
            )}
          </div>

          {/* Filter Button with Badge */}
          <div className="relative">
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => setIsFilterOpen(true)}
              className={activeFilterCount > 0 ? 'border-indigo-500 text-indigo-600 dark:text-indigo-400' : ''}
            >
              <Filter className="w-4 h-4 mr-2" />
              Filters
              {activeFilterCount > 0 && (
                <span className="ml-2 inline-flex items-center justify-center px-2 py-0.5 text-xs font-bold text-white bg-indigo-600 rounded-full">
                  {activeFilterCount}
                </span>
              )}
            </Button>
          </div>

          {/* Clear Filters */}
          {activeFilterCount > 0 && (
            <Button 
              variant="ghost" 
              size="sm"
              onClick={handleClearFilters}
              className="text-gray-600 dark:text-gray-400"
            >
              <X className="w-4 h-4 mr-1" />
              Clear
            </Button>
          )}
        </div>

        {/* Active Filters Display */}
        {activeFilterCount > 0 && (
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm text-gray-600 dark:text-gray-400">
              Active filters:
            </span>
            {activeFilters.status.length > 0 && (
              <span className="inline-flex items-center gap-1 px-3 py-1 text-sm font-medium text-indigo-700 dark:text-indigo-300 bg-indigo-100 dark:bg-indigo-900/40 rounded-full">
                Status: {activeFilters.status.join(', ')}
                <button
                  onClick={() => setActiveFilters(prev => ({ ...prev, status: [] }))}
                  className="ml-1 hover:bg-indigo-200 dark:hover:bg-indigo-800 rounded-full p-0.5"
                >
                  <X className="w-3 h-3" />
                </button>
              </span>
            )}
            {activeFilters.priority.length > 0 && (
              <span className="inline-flex items-center gap-1 px-3 py-1 text-sm font-medium text-indigo-700 dark:text-indigo-300 bg-indigo-100 dark:bg-indigo-900/40 rounded-full">
                Priority: {activeFilters.priority.join(', ')}
                <button
                  onClick={() => setActiveFilters(prev => ({ ...prev, priority: [] }))}
                  className="ml-1 hover:bg-indigo-200 dark:hover:bg-indigo-800 rounded-full p-0.5"
                >
                  <X className="w-3 h-3" />
                </button>
              </span>
            )}
            {activeFilters.assignee && (
              <span className="inline-flex items-center gap-1 px-3 py-1 text-sm font-medium text-indigo-700 dark:text-indigo-300 bg-indigo-100 dark:bg-indigo-900/40 rounded-full">
                Assignee: {activeFilters.assignee === 'unassigned' ? 'Unassigned' : teamMembers.find(m => m.user_id === activeFilters.assignee)?.name}
                <button
                  onClick={() => setActiveFilters(prev => ({ ...prev, assignee: '' }))}
                  className="ml-1 hover:bg-indigo-200 dark:hover:bg-indigo-800 rounded-full p-0.5"
                >
                  <X className="w-3 h-3" />
                </button>
              </span>
            )}
            {(activeFilters.dateFrom || activeFilters.dateTo) && (
              <span className="inline-flex items-center gap-1 px-3 py-1 text-sm font-medium text-indigo-700 dark:text-indigo-300 bg-indigo-100 dark:bg-indigo-900/40 rounded-full">
                Date: {activeFilters.dateFrom && new Date(activeFilters.dateFrom).toLocaleDateString()} - {activeFilters.dateTo && new Date(activeFilters.dateTo).toLocaleDateString()}
                <button
                  onClick={() => setActiveFilters(prev => ({ ...prev, dateFrom: '', dateTo: '' }))}
                  className="ml-1 hover:bg-indigo-200 dark:hover:bg-indigo-800 rounded-full p-0.5"
                >
                  <X className="w-3 h-3" />
                </button>
              </span>
            )}
          </div>
        )}

        {/* Tickets Table */}
        <TicketsTable
          tickets={tickets}
          loading={loading}
          onTicketClick={handleTicketClick}
          onSort={handleSort}
          sortBy={sortBy}
          sortDirection={sortDirection}
        />

        {/* Pagination */}
        {!loading && tickets.length > 0 && (
          <Pagination
            currentPage={currentPage}
            totalItems={total}
            itemsPerPage={itemsPerPage}
            onPageChange={handlePageChange}
            onItemsPerPageChange={handleItemsPerPageChange}
            itemsPerPageOptions={[10, 20, 25, 50, 100]}
          />
        )}

        {/* Filter Panel */}
        <TicketFilters
          filters={activeFilters}
          onFiltersChange={handleFiltersChange}
          onClear={handleClearFilters}
          teamMembers={teamMembers}
          isOpen={isFilterOpen}
          onClose={() => setIsFilterOpen(false)}
        />

        {/* Create Ticket Modal */}
        <CreateTicketModal
          isOpen={isCreateModalOpen}
          onClose={() => setIsCreateModalOpen(false)}
          onSubmit={handleTicketSubmit}
          users={mockUsers}
          availableTags={availableTags}
        />
      </div>
    </SupportTabLayout>
  );
};

export default TicketsTab;
